import TableHeader from "@commons/Table/TableHeader";
import Table from "@commons/Table/TableContent";
import AddIcon from "@mui/icons-material/Add";
import { usePage } from "@hooks/usePage";
import { useNavigate } from "react-router-dom";
import type { RegistersFilters } from "@services/registers/list/type";
import { useGetRegisters } from "@services/registers/list";
import { RegistersColumns } from "@typings/entities/registers/table";
import {
  prepareRegistersTable,
  type RegisterItemListWithLabels,
} from "@utils/translator/registers";
import { config } from "@/config";
import { useCallback, useState } from "react";
import { toast } from "react-toastify";
import { useExportRegisters } from "@hooks/exports/useExportRegisters";
import { usePermissions } from "@hooks/usePermissions";
import type { actionsType } from "@typings/components/table";

function TableView({ appliedFilters }: { appliedFilters: RegistersFilters }) {
  const { openModal, setDataModal } = usePage();
  const navigate = useNavigate();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(config.table.defaultPageSize);
  const { handleExport, isLoading: isExporting } = useExportRegisters();
  const { can } = usePermissions();

  const {
    error,
    isLoading,
    data: registers,
  } = useGetRegisters({
    ...appliedFilters,
    limit: rowsPerPage,
    page: page + 1,
  });

  const heads = RegistersColumns;

  const handleEdit = (register: RegisterItemListWithLabels) => {
    setDataModal({ id: register.id });
    openModal("editRegister");
  };

  const handleExportRegisters = (filters: RegistersFilters) => async () => {
    handleExport(filters);
  };

  const handleView = (register: RegisterItemListWithLabels) => {
    navigate(`/registers/${register.id}`);
  };

  const handlePageChange = useCallback((_: unknown, newPage: number) => {
    setPage(newPage);
  }, []);

  const handleRowsPerPageChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setRowsPerPage(parseInt(event.target.value, 10));
      setPage(0);
    },
    [],
  );

  if (error) {
    toast.error("Erro ao carregar as configurações");
  }

  const preparedRegisters = prepareRegistersTable(registers?.items || []);

  const canCreateRegister = can("create", "registers");
  const canUpdateRegister = can("update", "registers");

  const actions = ["view"] as actionsType;
  if (canUpdateRegister) {
    actions.push("edit");
  }

  return (
    <div className="w-full">
      <div className="w-full mt-6 flex flex-col justify-center items-center text-center bg-white rounded-lg shadow-md p-4">
        <TableHeader.Root>
          {canCreateRegister && (
            <TableHeader.Button
              customVariant="secondary"
              startIcon={<AddIcon />}
              onClick={() => openModal("newRegister")}
            >
              Novo Registro
            </TableHeader.Button>
          )}
          <TableHeader.Button
            customVariant="dark"
            onClick={handleExportRegisters(appliedFilters)}
            disabled={isExporting}
          >
            {isExporting ? "Exportando..." : "Exportar .csv"}
          </TableHeader.Button>
        </TableHeader.Root>

        <Table.Root isLoading={isLoading}>
          <Table.Head heads={heads} actions={true} />
          <Table.Body<RegisterItemListWithLabels>
            rows={preparedRegisters}
            columns={heads.map((head) => head.id)}
            actions={actions}
            onView={handleView}
            onEdit={handleEdit}
          />
          <Table.Footer
            colSpan={heads.length}
            count={registers?.meta.totalItems ?? 0}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handlePageChange}
            onRowsPerPageChange={handleRowsPerPageChange}
          />
        </Table.Root>
      </div>
    </div>
  );
}

export default TableView;
